import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Camera, ArrowLeft, X, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { photoAlbums } from "../mock/mock";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

// Group albums by year & month
const groupByYearMonth = (albums) => {
  const sorted = [...albums].sort((a, b) => new Date(b.date) - new Date(a.date));
  const groups = {};
  sorted.forEach((a) => {
    const d = new Date(a.date);
    const year = d.getFullYear();
    const month = d.toLocaleDateString("en-IN", { month: "long" });
    const key = `${year}-${month}`;
    if (!groups[key]) groups[key] = { year, month, albums: [] };
    groups[key].albums.push(a);
  });
  return Object.values(groups);
};

const AlbumsPage = () => {
  const { id } = useParams();
  const [year, setYear] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const years = useMemo(() => {
    const y = ["All", ...Array.from(new Set(photoAlbums.map((a) => new Date(a.date).getFullYear().toString())))];
    return y;
  }, []);

  const grouped = useMemo(() => {
    const filtered = year === "All" ? photoAlbums : photoAlbums.filter((a) => new Date(a.date).getFullYear().toString() === year);
    return groupByYearMonth(filtered);
  }, [year]);

  // Single album view
  if (id) {
    const album = photoAlbums.find((a) => a.id === id);
    if (!album) {
      return (
        <div className="py-32 text-center">
          <h2 className="font-display text-3xl font-bold text-stone-800 mb-4">Album not found</h2>
          <Link to="/albums">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full">Back to Albums</Button>
          </Link>
        </div>
      );
    }
    const currentIdx = album.photos.findIndex((p) => p === lightbox);

    return (
      <div>
        <section className="pt-12 pb-8 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
          <div className="container mx-auto px-4">
            <Link to="/albums" className="inline-flex items-center gap-2 text-orange-700 hover:text-orange-800 font-semibold text-sm mb-6">
              <ArrowLeft className="w-4 h-4" /> All Albums
            </Link>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm text-orange-700 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="font-semibold">{formatDate(album.date)}</span>
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-2">{album.title}</h1>
                <p className="text-stone-600 max-w-2xl">{album.description}</p>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-orange-100">
                <Camera className="w-4 h-4 text-orange-600" />
                <span className="font-semibold text-sm text-stone-700">{album.photos.length} photos</span>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {album.photos.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(p)}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all"
                >
                  <img src={p} alt={`${album.title} - ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-orange-900/0 group-hover:bg-orange-900/20 transition-all" />
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Lightbox */}
        {lightbox && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
            <button className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/40" onClick={() => setLightbox(null)}>
              <X className="w-5 h-5" />
            </button>
            {currentIdx > 0 && (
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/40"
                onClick={(e) => { e.stopPropagation(); setLightbox(album.photos[currentIdx - 1]); }}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            {currentIdx < album.photos.length - 1 && (
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/40"
                onClick={(e) => { e.stopPropagation(); setLightbox(album.photos[currentIdx + 1]); }}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
            <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={lightbox} alt="Preview" className="w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl" />
              <p className="text-center text-white/70 mt-4 text-sm">{currentIdx + 1} of {album.photos.length}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Albums listing
  return (
    <div>
      <section className="pt-16 pb-12 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Photo Albums</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            Memories, <span className="text-orange-600">Organized by Date</span>
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">A chronological journey through our milestones — from bhumi pujan to grand launch and beyond.</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          {/* Year filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all ${
                  year === y
                    ? "bg-orange-600 text-white border-orange-600"
                    : "bg-white text-stone-700 border-stone-200 hover:border-orange-300"
                }`}
              >
                {y}
              </button>
            ))}
          </div>

          {/* Grouped albums */}
          <div className="max-w-6xl mx-auto space-y-14">
            {grouped.map((group) => (
              <div key={`${group.year}-${group.month}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex flex-col items-center bg-gradient-to-br from-orange-500 to-amber-500 text-white rounded-2xl px-5 py-3 shadow-lg">
                    <span className="text-xs uppercase tracking-widest opacity-90">{group.month}</span>
                    <span className="font-display font-bold text-2xl leading-none">{group.year}</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-orange-300 to-transparent" />
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.albums.map((a) => (
                    <Link
                      key={a.id}
                      to={`/albums/${a.id}`}
                      className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-orange-100"
                    >
                      <div className="relative h-56 overflow-hidden">
                        <img src={a.coverImage} alt={a.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-orange-950/80 via-orange-900/20 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <p className="text-white/80 text-xs uppercase tracking-widest">{formatDate(a.date)}</p>
                          <h3 className="text-white font-display font-bold text-lg mt-1 line-clamp-2">{a.title}</h3>
                        </div>
                        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm text-orange-700 text-xs font-bold px-3 py-1.5 rounded-full shadow flex items-center gap-1">
                          <Camera className="w-3.5 h-3.5" /> {a.photoCount}
                        </div>
                      </div>
                      <div className="p-5">
                        <p className="text-sm text-stone-600 line-clamp-2">{a.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            {grouped.length === 0 && (
              <div className="text-center py-16">
                <Camera className="w-12 h-12 text-orange-300 mx-auto mb-3" />
                <p className="text-stone-500">No albums in this year.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AlbumsPage;
